import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Player, Position } from '../../types';

interface DraggablePlayerRowProps {
  player: Player;
  rank: number;
  position: Position;
  previewRating: number;
}

export function DraggablePlayerRow({
  player,
  rank,
  position,
  previewRating,
}: DraggablePlayerRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Get calculated rating for the current position
  const calculatedRating = position === 'FWD'
    ? player.calculated?.fwd_rating
    : position === 'MID'
    ? player.calculated?.mid_rating
    : position === 'DEF'
    ? player.calculated?.def_rating
    : player.calculated?.gk_rating;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-4 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg
        ${isDragging ? 'opacity-50 shadow-xl scale-105 z-50' : 'hover:bg-gray-50 dark:hover:bg-gray-750'}
        transition-all duration-200
      `}
    >
      {/* Rank Number */}
      <div className="flex-shrink-0 w-12 text-center">
        <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
          #{rank}
        </span>
      </div>

      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        aria-label="Drag to reorder"
      >
        <svg
          className="w-5 h-5 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
        </svg>
      </button>

      {/* Player Name */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
          {player.name}
        </div>
      </div>

      {/* Calculated Rating */}
      <div className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
        Calc: <span className="font-semibold">{calculatedRating?.toFixed(1) || '-'}</span>
      </div>

      {/* Arrow */}
      <div className="flex-shrink-0 text-gray-400">
        →
      </div>

      {/* Preview Manual Rating */}
      <div className="flex-shrink-0 w-16 text-right">
        <span className="text-lg font-bold text-green-600 dark:text-green-400">
          {previewRating.toFixed(1)}
        </span>
      </div>
    </div>
  );
}
